'use client';
import { BackgroundVisibility, useMyAvatar, usePage, useSettings } from '@capsicum/common';
import { type ComponentType, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';

import { useDrawer } from '../../hooks/drawer/useDrawer';
import { useRouter } from '../../hooks/router/useCustomRouter';
import { Avatar } from '../atoms/avatar/Avatar';
import { Reversible } from '../atoms/reversible/Reversible';
import { TopBar } from '../atoms/top-bar/TopBar';
import { BurgerIcon } from '../icons';
import { ChevronLeftIcon } from '../icons/ChevronLeft';

type AppTopBarProps = {
	customTopBar?: JSX.Element;
	SSUBalance?: ComponentType;
};

export const AppTopBar = ({ customTopBar, SSUBalance }: AppTopBarProps) => {
	const {
		pageTitle,
		pageType,
		showTopBarRPBalance,
		topBarBackgroundVisibility,
		topBarHeaderLeft,
		topBarHeaderRight
	} = usePage();
	const avatar = useMyAvatar()?.data?.avatars?.find(x => x?.imageType?.type === 'avatar:default')?.url;
	const { colors } = useTheme() || {};
	const { openDrawer } = useDrawer();
	const { back } = useRouter();
	const {
		settings: { isSideBarWalletEnabled }
	} = useSettings();

	const isTopbarOpaque = topBarBackgroundVisibility === BackgroundVisibility.Opaque;

	const headerRightIcons = useMemo(() => {
		if (!showTopBarRPBalance || !SSUBalance) return topBarHeaderRight;

		return [topBarHeaderRight === undefined ? null : topBarHeaderRight, <SSUBalance key="ssu-balance" />];
	}, [SSUBalance, showTopBarRPBalance, topBarHeaderRight]);

	const headerLeftIcons = useMemo(() => {
		if (pageType === 'subpage' || pageType === 'subpage-with-background') {
			return topBarHeaderLeft === undefined
				? [
						<Reversible key="profile">
							<S.ArrowBack
								color={isTopbarOpaque ? colors.text.main.enabled : colors.text.main.inverse}
								data-testid="home-top-bar-back"
								height={30}
								width={30}
								onClick={() => back()}
							/>
						</Reversible>
					]
				: topBarHeaderLeft;
		}

		return [
			!isSideBarWalletEnabled ? (
				<Avatar
					key="avatar"
					optimized
					size="XXS"
					src={avatar}
					testID="home-topBar-avatarIcon"
					onClick={() => openDrawer()}
				/>
			) : (
				<S.MenuIcon
					color={colors.icons.main.enabled}
					data-testid="home-topBar-burgerIcon"
					height={32}
					width={32}
					onClick={() => openDrawer()}
				/>
			)
		];
	}, [avatar, back, isSideBarWalletEnabled, openDrawer, pageType, topBarHeaderLeft, colors, isTopbarOpaque]);

	if (customTopBar) {
		return customTopBar;
	}

	if (pageType === 'background-view') {
		return null;
	}

	return (
		<TopBar
			backgroundColor={isTopbarOpaque ? colors.backgrounds.main.container : 'transparent'}
			borderColor={isTopbarOpaque ? colors.borders.main.divider : 'transparent'}
			headerLeft={headerLeftIcons}
			headerRight={headerRightIcons}
			testID="home-top-bar"
			textColor={isTopbarOpaque ? 'enabled' : 'inverse'}
			title={pageTitle}
			variant={pageType}
		/>
	);
};

namespace S {
	export const ArrowBack = styled(ChevronLeftIcon)`
		cursor: pointer;
	`;

	export const MenuIcon = styled(BurgerIcon)`
		cursor: pointer;
	`;
}
